import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DownloadSimple, X, QrCode, DeviceMobile } from "@phosphor-icons/react";
import QRCode from "qrcode";
import { skipWaitingAndReload } from "@/serviceWorkerRegistration";

// Dismissible "Install NaturePulse" chip + "Update available" chip.
// Never re-prompts a user who has dismissed within the last 30 days.

const DISMISS_KEY = "np:install:dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// URL that will be encoded in the QR shown on desktop. Point this at a
// splink.pro short link once one is created — e.g. "https://splink.pro/np-app".
// Defaults to the production domain so scanning always works.
const INSTALL_URL = "https://naturepulse.net?src=qr";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iP(ad|hone|od)/.test(navigator.userAgent) &&
  !window.MSStream;

const isDesktop = () => {
  if (typeof window === "undefined") return false;
  // Coarse pointer = touch device. Absence of coarse pointer + wide viewport = desktop.
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
  return !coarse && window.innerWidth >= 900;
};

export default function PWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIOSTip, setShowIOSTip] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const desktop = isDesktop();

  // Lazy-generate the QR (client-side) the first time the user opens it.
  useEffect(() => {
    if (!showQr || qrDataUrl) return;
    QRCode.toDataURL(INSTALL_URL, {
      margin: 1,
      width: 512,
      color: { dark: "#151312", light: "#F5EBDC" },
      errorCorrectionLevel: "M",
    }).then(setQrDataUrl).catch(() => setQrDataUrl(""));
  }, [showQr, qrDataUrl]);

  useEffect(() => {
    if (isStandalone()) return; // already installed

    // Suppress if recently dismissed
    const raw = localStorage.getItem(DISMISS_KEY);
    const dismissedRecently = raw && Date.now() - Number(raw) < DISMISS_TTL_MS;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissedRecently) setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari does not fire beforeinstallprompt; show manual tip.
    if (isIOS() && !dismissedRecently) {
      const t = setTimeout(() => setShowIOSTip(true), 8000);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const onUpdate = () => setUpdateReady(true);
    window.addEventListener("np:sw-update", onUpdate);
    return () => window.removeEventListener("np:sw-update", onUpdate);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowInstall(false); setShowIOSTip(false);
  };

  const doInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* noop */ }
    setDeferred(null); setShowInstall(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  return (
    <AnimatePresence>
      {updateReady && (
        <motion.div
          key="update"
          data-testid="pwa-update-chip"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          className="fixed z-[100] left-1/2 -translate-x-1/2 bottom-24 md:bottom-6 bg-[#151312] text-[#F5EBDC] px-4 py-2.5 rounded-full flex items-center gap-3 shadow-xl"
        >
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase">New data available</span>
          <button
            data-testid="pwa-update-refresh"
            onClick={skipWaitingAndReload}
            className="font-mono-tech text-[10px] tracking-[0.28em] uppercase underline"
          >
            Refresh
          </button>
        </motion.div>
      )}

      {(showInstall || showIOSTip) && !updateReady && (
        <motion.div
          key="install"
          data-testid="pwa-install-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: "spring", stiffness: 220, damping: 26 }}
          className="fixed z-[100] left-3 right-3 md:left-auto md:right-6 md:w-[380px] bottom-20 md:bottom-6 rounded-2xl bg-[#151312] text-[#F5EBDC] p-4 shadow-[0_20px_60px_rgba(0,0,0,.35)] border border-white/10"
        >
          <div className="flex items-start gap-3">
            <img src="/icon-192.png" alt="" width="44" height="44" className="rounded-xl shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C]">Install NaturePulse</div>
              <div className="font-serif-display text-lg leading-tight mt-1">Keep the planet&apos;s pulse one tap away.</div>
              {showIOSTip ? (
                <div className="mt-2 text-[13px] text-[#E5D8C4]/85 leading-snug">
                  Tap <span className="inline-block px-1.5 py-0.5 rounded border border-white/30 text-[11px] align-middle">Share</span> → <span className="whitespace-nowrap">Add to Home Screen</span>.
                </div>
              ) : (
                <div className="mt-1 text-[13px] text-[#E5D8C4]/75 leading-snug">Offline access, faster loads, quick shortcut to your local Pulse.</div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!showIOSTip && (
                  <button
                    data-testid="pwa-install-btn"
                    onClick={doInstall}
                    className="inline-flex items-center gap-2 rounded-full bg-[#E4A15C] text-[#151312] px-4 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
                  >
                    <DownloadSimple size={14} weight="bold" /> Install
                  </button>
                )}
                {desktop && (
                  <button
                    data-testid="pwa-install-qr-toggle"
                    onClick={() => setShowQr((v) => !v)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 text-[#F5EBDC] px-3 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-white/10"
                  >
                    <QrCode size={14} weight="bold" /> {showQr ? "Hide QR" : "On phone"}
                  </button>
                )}
                <button
                  data-testid="pwa-install-dismiss"
                  onClick={dismiss}
                  className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/70 hover:text-[#F5EBDC] px-2 py-2"
                >
                  Not now
                </button>
              </div>

              {desktop && showQr && (
                <div
                  data-testid="pwa-install-qr-panel"
                  className="mt-3 rounded-xl bg-[#F5EBDC] text-[#151312] p-3 flex items-start gap-3"
                >
                  <div className="w-[112px] h-[112px] shrink-0 rounded-lg overflow-hidden bg-[#F5EBDC] grid place-items-center">
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="Scan to open NaturePulse on your phone"
                        width="112" height="112"
                        data-testid="pwa-install-qr-image"
                      />
                    ) : (
                      <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">…</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1">
                      <DeviceMobile size={12} weight="fill" /> Install on your phone
                    </div>
                    <div className="mt-1 font-serif-display text-[15px] leading-snug">
                      Point your phone camera at this code.
                    </div>
                    <div className="mt-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] break-all">
                      naturepulse.net
                    </div>
                    <div className="mt-1.5 text-[11px] text-[#3A3830]">
                      Then tap <b>Add to Home Screen</b> (iPhone) or <b>Install app</b> (Android).
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button aria-label="Close" onClick={dismiss} className="opacity-70 hover:opacity-100">
              <X size={16} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
